import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';
import CommonSalesOrderInternationalFields from "../PumpInternatinalSO/CommonSalesOrderInternationalFields";

const PumpInternationalSOConversion = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    customer: null,
    customerDetails: {
      contactperson: '',
      contactno: '',
      email: '',
      freight: '',
      packingandforwarding: '',
      paymentterm: '',
      incoterms: ''
    },
    customerMultipleAddress: null,
    branch: null,
    warehouse: null,
    quotationno: '',
    quotationdate: '',
    amendmentno: '',
    amendmentdate: '',
    customerpono: '',
    customerpodate: '',
    rows: [],
    exchangeRate: 1,
    exportchargescurrency: 0,
    exportpreperationcharges: '',
    selectedCurrency: " "

  });

  const [sono, setsono] = useState('');
  const [sodate, setsodate] = useState('');
  const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
  const [isEnquiryValid, setIsEnquiryValid] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);


  const checksono = async (number) => {
    if (!number) {
      setEnquiryValidationMsg('');
      setIsEnquiryValid(false);
      return;
    }


    if (!/^[A-Za-z0-9-]+$/.test(number)) {
      setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
      setIsEnquiryValid(false);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}salesOrder/check/${number}`, {
        headers: authHeader(),
      });

      if (response.data === true) {
        setEnquiryValidationMsg('❌ This sales order number already exists.');
        setIsEnquiryValid(false);
      } else if (response.data === false) {
        setEnquiryValidationMsg('✅ This sales order number is available.');
        setIsEnquiryValid(true);
      } else {
        setEnquiryValidationMsg('Unexpected response from server.');
        setIsEnquiryValid(false);
      }
    } catch (error) {
      console.error("Error checking enquiry number:", error);
      setEnquiryValidationMsg('❌ Error validating sales order number.');
      setIsEnquiryValid(false);
    }
  };


  useEffect(() => {
    const debouncedCheck = debounce(() => {
      checksono(sono);
    }, 400);

    debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [sono]);


  // Fetch existing sales order data
  useEffect(() => {
    const fetchSalesOrderData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}quotation/findById/${id}`, {
          headers: authHeader()
        });

        const data = response.data;

        // Format the data for the form
        setsono(data.sono);
        setsodate(data.sodate?.split('T')[0] || '');

        setFormData({
          customer: data.customer ? {
            value: data.customer.id,
            label: data.customer.companyname
          } : null,
          customerDetails: {
            contactperson: data.contactperson || '',
            contactno: data.contactno || '',
            email: data.email || '',
            freight: data.freight || '',
            packingandforwarding: data.packingandforwarding || '',
            paymentterm: data.paymentterm || '',
            incoterms: data.incoterms || ''
          },
          customerMultipleAddress: data.customeraddress ? {
            value: data.customeraddress.id,
            label: data.customeraddress.customeraddress
          } : null,
          branch: data.branch ? {
            value: data.branch.id,
            label: data.branch.branchname
          } : null,
          warehouse: data.warehouse ? {
            value: data.warehouse.id,
            label: data.warehouse.warehousename
          } : null,
          quotationno: data.quotationno || '',
          quotationdate: data.quotationdate?.split('T')[0] || '',
          amendmentno: data.amendmentno || '',
          amendmentdate: data.amendmentdate?.split('T')[0] || '',
          customerpono: data.customerpono || '',
          customerpodate: data.customerpodate?.split('T')[0] || '',
          rows: data.quotationItems?.map(item => ({
            value: item.product.id,
            label: item.product.description,
            description: item.product.description,
            productremarks: item.productremarks || '',
            quantity: item.quantity || 0,
            unitratecurrency: item.unitratecurrency || 0,
            totalcurrency: item.totalcurrency || 0,
            exchangerate: item.exchangerate || data.salesorderexchangerate || 1,
            unitRateInr: item.unitrateinr || 0,
            totalInr: item.totalinr || 0,
            hsnsacno: item.hsnsacno || '',
            partno: item.partno || '',
            serialnumber: item.serialnumber || '',
            modelnumber: item.modelnumber || '',
            tagnumber: item.tagnumber || '',
            moc: item.moc || '',
            itemcode: item.itemcode || '',
            uom: item.uom || ''
          })) || [],
          exchangeRate: data.quotationexchangerate || 1,
          exportchargescurrency: data.exportpreparationchargescurrency || 0,
          exportpreperationcharges: data.exportpreperationcharges || '',
          selectedCurrency: data.salesordercurrency || "$ (USD)"
        });

      } catch (error) {
        console.error("Error fetching sales order data", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch sales order data. Please try again.",
        });
        navigate("/salesOrderIndexTable/1");
      } finally {
        setIsFetching(false);
      }
    };

    fetchSalesOrderData();
  }, [id, navigate]);

  // Handle data changes from child component
  const handleDataChange = (newData) => {
    setFormData(prev => ({
      ...prev,
      ...newData,
      rows: newData.rows || prev.rows
    }));
  };

  // Update sales Order
  const handleSubmit = async () => {
    setIsLoading(true);
    setFormSubmitted(true);
    if (!isEnquiryValid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please enter a valid enquiry number before submitting.',
      });
      return;
    }

    const {
      customer,
      customerDetails,
      customerMultipleAddress,
      branch,
      warehouse,
      quotationno,
      quotationdate,
      amendmentno,
      amendmentdate,
      customerpono,
      customerpodate,
      rows,
      exchangeRate,
      exportchargescurrency,
      exportpreperationcharges,
      selectedCurrency
    } = formData;

    // Validation
    if (!customer) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please select a customer',
      });
      setIsLoading(false);
      return;
    }

    if (rows.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one product',
      });
      setIsLoading(false);
      return;
    }

    // Calculate totals
    const misccharges = exportchargescurrency * exchangeRate;
    const netTotalInr = rows.reduce((total, row) => total + (row.totalInr || 0), 0);
    const netTotalCurrency = rows.reduce((total, row) => total + (row.totalcurrency || 0), 0);
    const grandTotalInr = netTotalInr + misccharges;
    const grandTotalCurrency = netTotalCurrency + exportchargescurrency;

    // Prepare sales order items
    const salesOrderItems = rows.map(row => ({
      product: { id: row.value },
      productremarks: row.productremarks || '',
      quantity: row.quantity || 0,
      unitrateinr: row.unitRateInr || 0,
      totalinr: row.totalInr || 0,
      unitratecurrency: row.unitratecurrency || 0,
      totalcurrency: row.totalcurrency || 0,
      exchangerate: row.exchangerate || exchangeRate,
      itemcode: row.itemcode || '',
      hsnsacno: row.hsnsacno || '',
      serialnumber: row.serialnumber || '',
      modelnumber: row.modelnumber || '',
      tagnumber: row.tagnumber || '',
      moc: row.moc || '',
      partno: row.partno || '',
      uom: row.uom || '',
      currency: selectedCurrency,
    }));

    // Prepare main Sales Order data
    const salesOrderData = {
      salesordertype: "Pump Spare",
      salesordercategory: "International",
      quotationno,
      quotationdate,
      amendmentno,
      amendmentdate,
      customerpono,
      customerpodate,
      sono,
      sodate,
      salesOrderItems,
      customer: { id: customer.value },
      customeraddress: customerMultipleAddress ? { id: customerMultipleAddress.value } : null,
      contactperson: customerDetails.contactperson,
      contactno: customerDetails.contactno,
      email: customerDetails.email,
      freight: customerDetails.freight,
      packingandforwarding: customerDetails.packingandforwarding,
      paymentterm: customerDetails.paymentterm,
      incoterm: customerDetails.incoterms,
      branch: branch ? { id: branch.value } : null,
      warehouse: warehouse ? { id: warehouse.value } : null,
      salesordercurrency: selectedCurrency,
      salesorderexchangerate: exchangeRate,
      grandtotalinr: grandTotalInr,
      grandtotalcurrency: grandTotalCurrency,
      exportpreperationchargescurrency: exportchargescurrency,
      exportpreperationcharges,
      nettotalinr: netTotalInr,
      nettotalcurrency: netTotalCurrency,
    };

    try {
      await axios.post(`${BASE_URL}salesOrder/quotationToSO/${id}`, salesOrderData, {
        headers: authHeader()
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Sales Order updated successfully!',
      });
      navigate(`/salesOrderIndexTable/1`);
    } catch (error) {
      console.error("Error updating Sales Order:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update Sales Order. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="container text-center py-5">
        <Spinner animation="border" />
        <p>Loading sales order details...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <Form>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Sales Order Number: *</Form.Label>
              <Form.Control
                type="text"
                value={sono}
                onChange={e => {
                  setsono(e.target.value);
                  setFormSubmitted(false);
                }}
                isInvalid={
                  (formSubmitted && !isEnquiryValid) ||
                  enquiryValidationMsg.includes('exists') ||
                  enquiryValidationMsg.includes('Error') ||
                  enquiryValidationMsg.includes('allowed')
                }
                required
              />
              {enquiryValidationMsg && (
                <Form.Control.Feedback type={
                  enquiryValidationMsg.includes('exists') ||
                    enquiryValidationMsg.includes('Error') ||
                    enquiryValidationMsg.includes('allowed') ?
                    "invalid" : "valid"
                }>
                  {enquiryValidationMsg}
                </Form.Control.Feedback>
              )}
              {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid enquiry number
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Sales Order Date</Form.Label>
              <Form.Control
                type="date"
                value={sodate}
                onChange={(e) => setsodate(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <CommonSalesOrderInternationalFields
          onDataChange={handleDataChange}
          initialData={formData}
          isUpdate={true}
        />

        <br />
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-primary me-2"
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" />
                Updating...
              </>
            ) : 'CONVERT SALES ORDER'}
          </button>

          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate(`/salesOrderView/${id}`)}
            disabled={isLoading}
          >
            CANCEL
          </button>
        </div>
      </Form>
    </div>
  );
};

export default PumpInternationalSOConversion;